import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import AppShell from "@/components/shell/AppShell";
import PageHead from "@/components/PageHead";
import { Input } from "@/components/ui/input";
import CertCard from "@/components/certifications/CertCard";
import { loadCertifications, resolveCareerIds, type Certification } from "@/lib/certifications";
import { useCareerDna } from "@/hooks/useCareerDna";

const LEVELS = ["All", "Foundation", "Associate", "Expert"];

export default function Certifications() {
  const location = useLocation();
  const { data: dna } = useCareerDna();
  const [all, setAll] = useState<Certification[]>([]);
  const [mine, setMine] = useState<Set<string>>(new Set());
  const [q, setQ] = useState("");
  const [level, setLevel] = useState("All");
  const [provider, setProvider] = useState("All");
  const [track, setTrack] = useState("All");
  const [onlyForMe, setOnlyForMe] = useState(false);

  useEffect(() => { loadCertifications().then(setAll).catch(() => setAll([])); }, []);
  useEffect(() => {
    if (!dna?.matches?.length) return;
    resolveCareerIds(dna.matches).then((ids) => setMine(new Set(ids)));
  }, [dna]);

  const providers = useMemo(() => ["All", ...Array.from(new Set(all.map((c) => c.provider))).sort()], [all]);
  const tracks = useMemo(() => ["All", ...Array.from(new Set(all.flatMap((c) => c.tracks))).sort()], [all]);
  const isForMe = (c: Certification) => c.careers.some((id) => mine.has(id));

  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return all
      .filter((c) => level === "All" || c.level === level)
      .filter((c) => provider === "All" || c.provider === provider)
      .filter((c) => track === "All" || c.tracks.includes(track))
      .filter((c) => !onlyForMe || isForMe(c))
      .filter((c) => !s || [c.name, c.provider, ...c.tracks, ...c.tools].join(" ").toLowerCase().includes(s))
      .sort((a, b) => Number(isForMe(b)) - Number(isForMe(a)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all, q, level, provider, track, onlyForMe, mine]);

  const select = "h-12 rounded-2xl border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary";

  return (
    <AppShell title="Certifications">
      <PageHead
        title="NSDC-approved certifications | WeKIT"
        description="Globally recognised, industry-certified micro-credentials approved by NSDC, matched to the careers they open up."
        path="/certifications"
      />
      <div className="space-y-6">
        <header className="space-y-2">
          <h1 className="font-display text-3xl font-bold text-foreground sm:text-4xl">Certifications</h1>
          <p className="max-w-2xl text-muted-foreground">
            {all.length || "200+"} industry certifications approved by NSDC, Government of India — each matched to the careers it helps you reach.
          </p>
        </header>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Input aria-label="Search certifications" placeholder="Search a skill, tool or provider" value={q}
            onChange={(e) => setQ(e.target.value)} className="h-12 rounded-2xl sm:col-span-2 lg:col-span-4" />
          <select aria-label="Level" className={select} value={level} onChange={(e) => setLevel(e.target.value)}>
            {LEVELS.map((l) => <option key={l} value={l}>{l === "All" ? "All levels" : l}</option>)}
          </select>
          <select aria-label="Provider" className={select} value={provider} onChange={(e) => setProvider(e.target.value)}>
            {providers.map((p) => <option key={p} value={p}>{p === "All" ? "All providers" : p}</option>)}
          </select>
          <select aria-label="Learning track" className={select} value={track} onChange={(e) => setTrack(e.target.value)}>
            {tracks.map((t) => <option key={t} value={t}>{t === "All" ? "All tracks" : t}</option>)}
          </select>
          {mine.size > 0 && (
            <label className="inline-flex min-h-12 items-center gap-2 rounded-2xl bg-muted/40 px-4 text-sm text-foreground">
              <input type="checkbox" checked={onlyForMe} onChange={(e) => setOnlyForMe(e.target.checked)} className="h-5 w-5" />
              Only ones for my matches
            </label>
          )}
        </div>

        <p className="text-sm text-muted-foreground" aria-live="polite">{list.length} certifications</p>
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((c) => (
            <CertCard key={c.id} cert={c} recommended={isForMe(c)} from={location.pathname} />
          ))}
        </ul>
      </div>
    </AppShell>
  );
}
